import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { userFirstNameSelector } from 'modules/user/selectors';
import { useAskAssociation } from './AskAssociationContext';
import { authFetch, chatbotConfig } from './chatbotSession';
import {
  SparkleIcon,
  SparkleIconSmall,
  SendIcon,
  PageShell,
  PageInner,
  PageTitleBar,
  PageTitleText,
  PageTitleBeta,
  UsageBar,
  UsageBadge,
  Overlay,
  Panel,
  Header,
  HeaderLeft,
  HeaderTitle,
  HeaderBetaTag,
  CloseBtn,
  WelcomeArea,
  WelcomeHeading,
  WelcomeSubtitle,
  SuggestionGrid,
  SuggestionCard,
  SuggestionCardArrow,
  MessagesArea,
  MessageGroup,
  BubbleRow,
  AvatarDot,
  Bubble,
  MarkdownBody,
  TypingIndicator,
  TypingDot,
  InlineSuggestionGrid,
  SuggestionsLoadingRow,
  SuggestionsLoadingDot,
  InputArea,
  TextareaWrapper,
  StyledTextarea,
  SendBtn,
  Disclaimer,
} from './AskAssociationPanel.style';

// Usage badge is hidden for now. Flip to true to re-enable the "used / limit"
// pill — all the /usage fetch + stream/suggestions parsing stays wired, so this
// const is the only switch.
const SHOW_USAGE_BADGE = false;

// Only http(s) links are rendered as anchors. This is defense-in-depth on top
// of react-markdown v5's built-in URL sanitizer — it drops javascript:, data:,
// vbscript:, etc. so a malicious citation URL from model output can't execute.
const isSafeHttpUrl = (url?: string): boolean => {
  if (!url) return false;
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://www.aicpa-cima.com';
    const { protocol } = new URL(url, base);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

// The assistant weaves reference links inline through its answer. Render each as
// an anchor that opens in a new tab (rel="noopener noreferrer" guards against
// reverse-tabnabbing). Unsafe/non-http(s) hrefs fall back to plain text.
// react-markdown v5 uses the `renderers` prop keyed by node type (`link`).
const markdownRenderers = {
  link: ({ href, title, children }: { href?: string; title?: string; children?: React.ReactNode }) =>
    isSafeHttpUrl(href) ? (
      <a href={href} title={title} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ) : (
      <>{children}</>
    ),
};

// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_RE = /[\x00-\x08\x0B-\x1F\x7F]/g;
const normalizeUserText = (text: string): string => text.replace(CONTROL_CHAR_RE, '').trim();

const STARTER_SUGGESTIONS = [
  'How do I renew my membership?',
  'What types of AICPA membership are available?',
  'What do the new quality management standards require my firm to have in place?',
  'What changed for domestic R&E expenditures under Section 174A?',
  'What CPE requirements do I need to maintain?',
  'What guidance is available on sustainability reporting and ISSB standards?',
];

interface Message {
  role: 'user' | 'assistant';
  content: string;
  suggestions?: string[];
}

export const AskAssociationPanel: React.FC<{ asPage?: boolean }> = ({ asPage = false }) => {
  const { isOpen: overlayOpen, close } = useAskAssociation();
  // Page mode renders the chat as a full route — always visible and independent
  // of the overlay open/close context.
  const isOpen = asPage || overlayOpen;
  const firstName = useSelector(userFirstNameSelector);

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  // Rolling 24h message usage for the current caller ("used / limit"), sourced
  // from the chatbot's rate limiter. Null until the first /usage fetch resolves.
  const [usage, setUsage] = useState<{ used: number; limit: number } | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (bottomRef.current && messages.length > 0) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Pull the current 24h usage whenever the panel opens so the badge shows the
  // count even before the first message. Best-effort: errors leave it hidden.
  useEffect(() => {
    if (!isOpen || !SHOW_USAGE_BADGE) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${chatbotConfig().apiUrl}/usage`, { method: 'GET' });
        if (!res.ok) return;
        const data = (await res.json()) as { used?: number; limit?: number };
        if (!cancelled && typeof data.used === 'number' && typeof data.limit === 'number') {
          setUsage({ used: data.used, limit: data.limit });
        }
      } catch {
        /* usage badge is best-effort; ignore fetch errors */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setInput('');
      setIsStreaming(false);
      setIsFetchingSuggestions(false);
      setLimitReached(false);
    }
  }, [isOpen]);

  useEffect(() => {
    // Esc-to-close only applies to the overlay, not the full-page route.
    if (!isOpen || asPage) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, close, asPage]);

  // Page mode: pin the chat to a fixed height (viewport minus the header) so the
  // message list scrolls internally instead of growing the whole page as answers stream in.
  const pageShellRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!asPage) return undefined;
    const el = pageShellRef.current;
    if (!el) return undefined;
    const applyHeight = () => {
      const top = el.getBoundingClientRect().top;
      // Subtract anything below the shell (e.g. the site footer) so header + chat +
      // footer fill the viewport exactly and the page itself never scrolls.
      let below = 0;
      let sib: Element | null = el.nextElementSibling;
      while (sib) {
        below += sib.getBoundingClientRect().height;
        sib = sib.nextElementSibling;
      }
      el.style.height = `${Math.max(320, window.innerHeight - top - below)}px`;
    };
    applyHeight();
    window.addEventListener('resize', applyHeight);
    return () => window.removeEventListener('resize', applyHeight);
  }, [asPage]);

  const fetchAndAttachSuggestions = useCallback(async (fullMsgs: Message[]) => {
    setIsFetchingSuggestions(true);
    try {
      // Strip the `suggestions` field so the backend only receives role + content
      const payload = fullMsgs.map(({ role, content }) => ({ role, content }));
      const res = await authFetch(`${chatbotConfig().apiUrl}/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: payload }),
      });
      if (!res.ok) return;
      const data = await res.json();
      // Suggestions do NOT count toward the quota (separate server bucket); the
      // response just echoes the current chat usage, so keep the badge in sync.
      if (data.usage && typeof data.usage.used === 'number' && typeof data.usage.limit === 'number') {
        setUsage({ used: data.usage.used, limit: data.usage.limit });
      }
      const fetched = (data.suggestions as string[]) || [];
      if (fetched.length === 0) return;
      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (updated[lastIdx]?.role === 'assistant') {
          updated[lastIdx] = { ...updated[lastIdx], suggestions: fetched };
        }
        return updated;
      });
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
    } finally {
      setIsFetchingSuggestions(false);
    }
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const normalized = normalizeUserText(text);
      if (!normalized || isStreaming || limitReached) return;

      const userMsg: Message = { role: 'user', content: normalized };
      const nextMessages = [...messages, userMsg];
      setMessages([...nextMessages, { role: 'assistant', content: '' }]);
      setInput('');
      setIsStreaming(true);

      try {
        const res = await authFetch(`${chatbotConfig().apiUrl}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: nextMessages.map(({ role, content }) => ({ role, content })) }),
        });

        if (res.status === 401) {
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: 'Your session has expired. Please refresh the page and try again.',
            };
            return updated;
          });
          setLimitReached(true);
          return;
        }

        if (res.status === 400) {
          const data = await res.json().catch(() => ({}));
          const detail = (data && (data.detail?.message || data.message)) || 'Your message was rejected. Please try again.';
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { role: 'assistant', content: detail };
            return updated;
          });
          return;
        }

        if (res.status === 429) {
          const data = await res.json().catch(() => ({}));
          const errorCode: string = data?.detail?.error || data?.error || 'conversation_limit_reached';
          const message: string =
            data?.detail?.message ||
            data?.message ||
            'This conversation has reached its length limit. Please start a new conversation to continue.';
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { role: 'assistant', content: message };
            return updated;
          });
          // Short-window throttling is transient — let the user try again shortly.
          // Daily cap and conversation cap are session-locking.
          if (errorCode !== 'rate_limit_short') {
            setLimitReached(true);
          }
          return;
        }

        if (!res.ok || !res.body) throw new Error('Request failed');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        let done = false;
        let assistantText = '';

        while (!done) {
          const { value, done: readerDone } = await reader.read();
          done = readerDone;
          if (value) {
            buf += decoder.decode(value, { stream: true });
            const lines = buf.split('\n');
            buf = lines.pop() || '';
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              const payload = line.slice(6);
              if (payload === '[DONE]') continue;
              try {
                const parsed = JSON.parse(payload) as {
                  text?: string;
                  citations?: unknown[];
                  usage?: { used?: number; limit?: number };
                };
                if (parsed.text) {
                  assistantText += parsed.text;
                  setMessages(prev => {
                    // Guard against a chunk arriving after the panel closed and
                    // reset `messages` to [] mid-stream (updated[-1] would throw).
                    if (prev.length === 0) return prev;
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    updated[updated.length - 1] = {
                      role: 'assistant',
                      content: (last ? last.content : '') + parsed.text,
                    };
                    return updated;
                  });
                }
                // The stream ends with a usage event so the badge updates the
                // moment the answer finishes.
                if (
                  parsed.usage &&
                  typeof parsed.usage.used === 'number' &&
                  typeof parsed.usage.limit === 'number'
                ) {
                  setUsage({ used: parsed.usage.used, limit: parsed.usage.limit });
                }
                // The `citations` event (all retrieved articles) is intentionally
                // NOT rendered: reference links are now woven inline into the answer
                // text, so a separate sources list would duplicate them. It stays
                // available here for non-visual use (e.g. analytics) if ever needed.
              } catch {
                // ignore malformed chunks
              }
            }
          }
        }

        await fetchAndAttachSuggestions([...nextMessages, { role: 'assistant', content: assistantText }]);
      } catch {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try again.',
          };
          return updated;
        });
      } finally {
        setIsStreaming(false);
      }
    },
    [messages, isStreaming, limitReached, fetchAndAttachSuggestions]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const inChatMode = messages.length > 0;
  const welcomeHeading = firstName ? `Hello, ${firstName}!` : 'How can I help you?';

  if (!isOpen) return null;

  const body = (
    <>
      {SHOW_USAGE_BADGE && usage && (
        <UsageBar>
          <UsageBadge
            warn={usage.limit - usage.used <= 10}
            title={`You've used ${usage.used} of ${usage.limit} messages in the last 24 hours`}
          >
            {usage.used} / {usage.limit} messages used · last 24h
          </UsageBadge>
        </UsageBar>
      )}
      {inChatMode ? (
          <MessagesArea>
            {messages.map((msg, i) => (
              <MessageGroup key={i}>
                <BubbleRow isUser={msg.role === 'user'}>
                  {msg.role === 'assistant' && (
                    <AvatarDot aria-hidden="true">
                      <SparkleIconSmall />
                    </AvatarDot>
                  )}
                  <Bubble isUser={msg.role === 'user'}>
                    {msg.role === 'user' ? (
                      msg.content
                    ) : msg.content ? (
                      <MarkdownBody>
                        <ReactMarkdown plugins={[gfm]} renderers={markdownRenderers}>
                          {msg.content}
                        </ReactMarkdown>
                      </MarkdownBody>
                    ) : isStreaming && i === messages.length - 1 ? (
                      <TypingIndicator aria-label="Waiting for response">
                        <TypingDot delay={0} />
                        <TypingDot delay={0.15} />
                        <TypingDot delay={0.3} />
                      </TypingIndicator>
                    ) : null}
                  </Bubble>
                </BubbleRow>

                {msg.role === 'assistant' && msg.suggestions && msg.suggestions.length > 0 && (
                  <InlineSuggestionGrid>
                    {msg.suggestions.map((s, j) => (
                      <SuggestionCard key={j} onClick={() => sendMessage(s)}>
                        <SuggestionCardArrow aria-hidden="true">→</SuggestionCardArrow>
                        {s}
                      </SuggestionCard>
                    ))}
                  </InlineSuggestionGrid>
                )}
              </MessageGroup>
            ))}

            {isFetchingSuggestions && (
              <SuggestionsLoadingRow>
                <SuggestionsLoadingDot delay={0} />
                <SuggestionsLoadingDot delay={0.15} />
                <SuggestionsLoadingDot delay={0.3} />
              </SuggestionsLoadingRow>
            )}

            <div ref={bottomRef} />
          </MessagesArea>
        ) : (
          <WelcomeArea>
            <WelcomeHeading>{welcomeHeading}</WelcomeHeading>
            <WelcomeSubtitle>Ask me anything about AICPA &amp; CIMA </WelcomeSubtitle>
            <SuggestionGrid>
              {STARTER_SUGGESTIONS.map((s, i) => (
                <SuggestionCard key={i} onClick={() => sendMessage(s)}>
                  <SuggestionCardArrow aria-hidden="true">→</SuggestionCardArrow>
                  {s}
                </SuggestionCard>
              ))}
            </SuggestionGrid>
          </WelcomeArea>
        )}

        <InputArea>
          <TextareaWrapper>
            <StyledTextarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                limitReached ? 'Conversation limit reached — please start a new one.' : 'Ask anything about AICPA & CIMA…'
              }
              rows={1}
              disabled={isStreaming || limitReached}
            />
          </TextareaWrapper>
          <SendBtn
            onClick={() => sendMessage(input)}
            disabled={isStreaming || limitReached || !normalizeUserText(input)}
            aria-label="Send"
          >
            <SendIcon />
          </SendBtn>
        </InputArea>
        <Disclaimer>
          Member AIQ is AI-generated, supports US English only, and may produce inaccurate information. Verify
          important details with official AICPA &amp; CIMA sources.
        </Disclaimer>
    </>
  );

  // Full-page route: centered column, no overlay/backdrop/close button.
  if (asPage) {
    return (
      <PageShell ref={pageShellRef}>
        <PageInner>
          <PageTitleBar>
            <SparkleIcon aria-hidden="true" />
            <PageTitleText>Member AIQ</PageTitleText>
            <PageTitleBeta>Beta</PageTitleBeta>
          </PageTitleBar>
          {body}
        </PageInner>
      </PageShell>
    );
  }

  // Slide-in overlay (legacy trigger path).
  return (
    <Overlay onClick={close}>
      <Panel onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderLeft>
            <SparkleIcon aria-hidden="true" />
            <HeaderTitle>Member AIQ</HeaderTitle>
            <HeaderBetaTag>Beta</HeaderBetaTag>
          </HeaderLeft>
          <CloseBtn onClick={close} aria-label="Close Member AIQ">&#x2715;</CloseBtn>
        </Header>
        {body}
      </Panel>
    </Overlay>
  );
};
